import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { z } from "zod";
import { adminAuthenticate } from "@/services/authApi";
import { isAuthenticated, isTokenExpired } from "@/lib/auth";
import { clearAuthToken } from "@/services/apiClient";
import { SmplifyLogo } from "@/components/SmplifyLogo";

const isPreviewEnv = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.includes("lovableproject.com") || h.includes("lovable.app");
})();

const loginSchema = z.object({
  email: z.string().trim().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  password: z.string().min(8, "Password must be at least 8 characters").max(128, "Password must be less than 128 characters")
});

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated() && !isTokenExpired()) {
      navigate("/getting-started", { replace: true });
    } else if (isAuthenticated() && isTokenExpired()) {
      // Stale/expired token in storage would otherwise cause a redirect loop
      // with PrivateRoute (Safari throws after >100 history.replaceState calls / 10s).
      clearAuthToken();
      try {
        localStorage.removeItem("smplify_tenant_id");
      } catch {
        // ignore storage errors (private mode, quota)
      }
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);

    try {
      const validationResult = loginSchema.safeParse({ email, password });
      if (!validationResult.success) {
        const errorMessage = validationResult.error.errors[0].message;
        setAuthError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      await adminAuthenticate({
        username: validationResult.data.email,
        password: validationResult.data.password,
      });

      toast.success("Login successful!");
      navigate("/getting-started", { replace: true });
    } catch (error: any) {
      let msg = error?.message || "Invalid credentials";
      if (msg === 'Failed to fetch' || msg.includes('NetworkError') || msg.includes('CORS')) {
        msg = 'Unable to reach the server. Please check your connection and try again.';
      }
      setAuthError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <main className="w-full max-w-7xl px-12 py-8 flex flex-col md:flex-row items-center justify-center gap-20">
        <div className="w-full md:w-1/2 flex justify-center">
          <SmplifyLogo
            size="login"
            // @ts-ignore
            fetchpriority="high"
            width="300"
            height="225"
          />
        </div>
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-lg border p-8">
            {isPreviewEnv && (
              <Alert className="mb-4">
                <AlertDescription className="text-xs">
                  Login may be unreliable in the Lovable preview. For production sign-in, use{" "}
                  <a
                    href="https://console.smplify.com/login"
                    className="text-primary hover:underline font-medium"
                  >
                    console.smplify.com
                  </a>
                  .
                </AlertDescription>
              </Alert>
            )}
            {authError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Please sign in to your account
              </p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full" />
              </div>
              <div className="space-y-2">
                <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full" />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Signing in..." : (<><LogIn className="w-4 h-4 mr-2" />Sign in</>)}
              </Button>
            </form>
            <div className="text-center space-y-2 text-sm text-muted-foreground">
              <p>
                <Link to="/forgot-password" className="text-primary hover:underline">Forgot your password?</Link>
              </p>
              <p>
                New user?{" "}
                <Link to="/register" className="text-primary hover:underline">Create an account</Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
